// deps
import { useReducer, Dispatch, useMemo, useCallback, useContext } from "react";

// internal deps
import { WalletPaymentMethodData } from "@swiggy-private/payment-ui";
import { VALID_WALLET_LINK_STATUSES, WalletApiFetchState } from "@swiggy-private/payment-core";
import { Result, Ok, Err } from "@swiggy-private/js-fp";
import { useMountedRef } from "@swiggy-private/core-ui";

// apis
import { getWalletBalance } from "@payment-client/apis/Wallet";

// reducers
import {
    getWalletBalanceReducer,
    GetWalletBalanceActionTypes,
    GetWalletBalanceAction,
    WalletBalanceState,
    defaultWalletBalanceStateValue,
} from "@payment-client/reducers/Wallet";

// interfaces
import { GetWalletBalanceResponse } from "@payment-client/interfaces/WalletApi";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// helpers
import { isRedirectFlow, getAllWalletMethods } from "@payment-client/helpers/WalletHelper";
import { WalletBalanceContextValue } from "@payment-client/contexts/WalletBalanceContext";

// contexts
import { ApiContext, CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS, PublicApiContext } from "@swiggy-private/checkout-core";
import { transformBalanceInfo } from "@payment-client/helpers/PaymentHelper";
import { getWalletBalancePublic } from "@payment-client/apis/publicWallet";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";

interface FetchWalletBalanceResponse {
    name: string;
    response: Result<GetWalletBalanceResponse, Error | string | unknown>;
}

const defaultBalanceResponse: GetWalletBalanceResponse = {
    linkStatus: VALID_WALLET_LINK_STATUSES.NEVER_LINKED,
    balance: 0,
    isOldWalletLinkFlow: false,
    mobileNumber: "",
};

async function fetchWalletBalance(
    apiHost: string,
    paymentMethod: WalletPaymentMethodData,
    isPublicApiEnabled = false,
): Promise<FetchWalletBalanceResponse> {
    try {
        if (paymentMethod.balanceInfo) {
            const balanceInfo = transformBalanceInfo(paymentMethod.balanceInfo);
            return {
                name: paymentMethod.name,
                response: Ok(balanceInfo),
            };
        }

        const getWalletBalanceFunction = isPublicApiEnabled ? getWalletBalancePublic : getWalletBalance;

        const response = await getWalletBalanceFunction(apiHost, paymentMethod.name);
        return {
            name: paymentMethod.name,
            response: Ok(response ?? defaultBalanceResponse),
        };
    } catch (err) {
        return { name: paymentMethod.name, response: Err(err) };
    }
}

function processFetchWalletBalanceResponse(
    { name, response }: FetchWalletBalanceResponse,
    dispatch: Dispatch<GetWalletBalanceAction>,
): void {
    response.match({
        ok: result => {
            const payload = { ...result, balance: Number(result.balance) || 0 };
            dispatch({ name, type: GetWalletBalanceActionTypes.SUCCESS, payload });
        },
        err: () => dispatch({ name, type: GetWalletBalanceActionTypes.ERROR }),
    });
}

function defaultReducerStateBuilder(paymentMethods: WalletPaymentMethodData[]): WalletBalanceState {
    return paymentMethods.reduce<WalletBalanceState>((acc, cur) => {
        acc[cur.name] = {
            ...defaultWalletBalanceStateValue,
            fetchState: isRedirectFlow(cur) ? WalletApiFetchState.SUCCESS : WalletApiFetchState.DEFAULT,
            payableAmount: cur?.payableAmount,
        };
        return acc;
    }, {});
}

interface WalletBalanceProps {
    paymentResponse: GetPaymentMethodsResponse;
}

export function useWalletBalance({ paymentResponse }: WalletBalanceProps): WalletBalanceContextValue {
    const isMountedRef = useMountedRef();
    const { host } = useContext(ApiContext);

    const publicApiConfig = useContext(PublicApiContext);

    // Memoize paymentMethods with a more stable dependency
    const paymentMethods = useMemo(() => getAllWalletMethods(paymentResponse.paymentGroup), [
        paymentResponse.paymentGroup,
    ]);
    const [reducerState, dispatch] = useReducer(getWalletBalanceReducer, paymentMethods, defaultReducerStateBuilder);
    const apiHost = host.unwrap();

    const getIsPublicApiEnabledForWallet = useCallback(
        (methodName: string) => {
            const result = isPublicApiEnabled(
                publicApiConfig || {},
                CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.WALLET_APIS,
                methodName.toLowerCase(),
            );
            return result;
        },
        [publicApiConfig],
    );

    const refetchBalance = useCallback(
        async (name: string): Promise<void> => {
            const method = paymentMethods.find(method => method.name === name);
            if (!method) {
                return;
            }

            const isPublicApiEnabledForMethod = getIsPublicApiEnabledForWallet(method.name.toLowerCase());

            dispatch({ type: GetWalletBalanceActionTypes.REQUEST, name });
            const response = await fetchWalletBalance(apiHost, method, isPublicApiEnabledForMethod);
            if (isMountedRef.current) {
                processFetchWalletBalanceResponse(response, dispatch);
            }
        },
        [apiHost, isMountedRef, paymentMethods, getIsPublicApiEnabledForWallet],
    );

    // Memoize the return value to prevent unnecessary re-renders of consumers
    const contextValue = useMemo(
        () => ({
            setState: dispatch,
            state: reducerState,
            refetch: refetchBalance,
        }),
        [reducerState, refetchBalance],
    );

    return contextValue;
}
